import React, { useEffect, useState } from 'react';
import { Route, useLocation } from 'react-router-dom';
import { matchRoutes, RouteConfig } from 'react-router-config';
import { Config, SearchStyle } from './Config';
import { getBaseUrl } from './utils/globals';
import { triggerCallback } from './clientApi';
import { useSelector } from './actions/types';
import usePageNav from './hooks/usePageNav';

import HomePage from './pages/home';
import CategoryPage from './pages/category';
import ArticlePage from './pages/article';
import SearchPage from './pages/search';
import Error404Page from './pages/error404';
import ExtraPage from './pages/extra';

type RouteConfigWithExtra = RouteConfig;

const baseUrl = getBaseUrl();
const baseRoutes: ReadonlyArray<RouteConfigWithExtra> = [
  {
    component: ArticlePage,
    path: `${baseUrl}/articles/:articleId`,
  },
  {
    component: CategoryPage,
    path: `${baseUrl}/categories/:categoryId`,
  },
  {
    component: HomePage,
    path: `${baseUrl}/`,
    exact: true,
  },
];

const searchRoute: RouteConfigWithExtra = {
  component: SearchPage,
  path: `${baseUrl}/search`,
};

export function getRoutes(config: Config): Array<RouteConfigWithExtra> {
  let routes = baseRoutes;
  // Add the search route if we are using results page routing.
  if (!config.searchStyle || config.searchStyle === SearchStyle.resultsPage) {
    routes = routes.concat(searchRoute);

    // Make sure there is a search page defined.
    if (!config.pages.search) {
      throw new Error(
        'Search page must be defined when not using dropdown search style'
      );
    }
  }

  // Wrap the Extra pages in our extra page component.
  const extraPages = (config.extraPages || []).map((r) => ({
    ...r,
    component: () => <ExtraPage page={r.component} />,
    path: `${baseUrl}/${r.path}`,
  }));

  return routes.concat(extraPages).concat({
    // 404 route needs to be the last one.
    component: Error404Page,
    path: '*',
  });
}

type AppRoutesProps = {
  routes: Array<RouteConfigWithExtra>;
};
const AppRoutes: React.FC<AppRoutesProps> = ({ children, routes }) => {
  const location = useLocation();
  const [oldLocation, setOldLocation] = useState(location);
  const currentPage = useSelector((state) => state.page.currentPage);
  const [oldCurrentPage, setOldCurrentPage] = useState(currentPage);
  const matchedRoutes = matchRoutes(routes, location.pathname);
  if (matchedRoutes.length === 0) {
    throw new Error("Not matched routes, this shouldn't happen");
  }

  usePageNav(matchedRoutes[0]);

  if (oldCurrentPage !== currentPage) {
    setOldCurrentPage(currentPage);
    setOldLocation(location);
  }

  useEffect(() => {
    // Fire off page:change event
    triggerCallback('page:change', {
      url: document.location.toString(),
    });

    // Scroll to top on route change
    window.scrollTo(0, 0);
  }, [currentPage]);
  return <Route location={oldLocation} render={() => children} />;
};

export default AppRoutes;
